import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays, ChevronDown } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const toYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseYMD = (s) => {
  if (!s) return undefined;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const fmtLong = (s) => {
  const d = parseYMD(s);
  return d ? d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "Select date";
};

// Premium single-day picker with Today / Yesterday presets. `max` (YYYY-MM-DD)
// caps the selectable range so only past/today can be chosen.
export default function SingleDatePicker({ value, max, onChange, testid = "single-date" }) {
  const [open, setOpen] = useState(false);
  const today = max || toYMD(new Date());
  const yest = (() => { const d = parseYMD(today); d.setDate(d.getDate() - 1); return toYMD(d); })();
  const maxDate = parseYMD(today);
  const pick = (ymd) => { onChange(ymd); setOpen(false); };
  const relLabel = value === today ? "Today" : value === yest ? "Yesterday" : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={`${testid}-trigger`} className="flex items-center gap-2 rounded-lg border border-sky bg-white px-3 py-2 text-sm font-medium text-navy transition-colors hover:bg-mist focus:outline-none focus:ring-2 focus:ring-loop/25">
          <CalendarDays className="h-4 w-4 text-loop" />
          <span>{fmtLong(value)}</span>
          {relLabel && <span className="rounded-full bg-loop/10 px-2 py-0.5 text-[10px] font-semibold text-loop">{relLabel}</span>}
          <ChevronDown className="h-3.5 w-3.5 text-ink/40" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0" data-testid={`${testid}-popover`}>
        <div className="flex">
          <div className="flex w-32 flex-col gap-1 border-r border-sky/50 p-2">
            <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-ink/40">Quick</p>
            <button data-testid={`${testid}-today`} onClick={() => pick(today)} className={`rounded-lg px-3 py-1.5 text-left text-sm font-medium transition-colors ${value === today ? "bg-loop text-white" : "text-navy hover:bg-mist"}`}>Today</button>
            <button data-testid={`${testid}-yesterday`} onClick={() => pick(yest)} className={`rounded-lg px-3 py-1.5 text-left text-sm font-medium transition-colors ${value === yest ? "bg-loop text-white" : "text-navy hover:bg-mist"}`}>Yesterday</button>
          </div>
          <Calendar
            mode="single"
            selected={parseYMD(value)}
            onSelect={(d) => d && pick(toYMD(d))}
            disabled={{ after: maxDate }}
            defaultMonth={parseYMD(value)}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
